import React, { useEffect, useRef } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { personalInfo } from '../data/mock';
import { ChevronDown, Sparkles } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Hero = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const taglineRef = useRef(null);
  const scrollIndicatorRef = useRef(null);
  const accentLineRef = useRef(null);
  const sparklesRef = useRef(null);
  const { currentColors } = useTheme();

  useEffect(() => {
    // Sparkles animation
    if (sparklesRef.current) {
      const sparkles = sparklesRef.current.children;
      Array.from(sparkles).forEach((sparkle, index) => {
        anime({
          targets: sparkle,
          opacity: [0, 1, 0],
          scale: [0, 1, 0],
          rotate: [0, 360],
          duration: 2000,
          delay: anime.random(0, 2000),
          loop: true,
          easing: 'easeInOutSine'
        });
      });
    }

    // Hero title character-by-character animation with enhanced effects
    const titleText = personalInfo.name;
    const titleElement = titleRef.current;
    
    titleElement.innerHTML = titleText.split('').map((char, index) => 
      `<span class="inline-block hero-char" style="opacity: 0; transform: translateY(60px) rotateX(45deg) scale(0.9);">${char === ' ' ? '&nbsp;' : char}</span>`
    ).join('');

    // Animate title characters with 3D effects - smoother animation
    anime({
      targets: titleElement.querySelectorAll('span'),
      opacity: [0, 1],
      translateY: [60, 0],
      rotateX: [45, 0],
      scale: [0.9, 1.05, 1],
      duration: 1200,
      delay: anime.stagger(50, { start: 4000 }),
      easing: 'easeOutCubic'
    });

    // Add glowing effect to title
    anime({
      targets: titleRef.current,
      textShadow: [
        `0 0 0px ${currentColors.accent}`,
        `0 0 30px ${currentColors.accent}40`,
        `0 0 0px ${currentColors.accent}`
      ],
      duration: 3000,
      delay: 5000,
      loop: true,
      easing: 'easeInOutSine'
    });

    // Add character hover animations - smoother transitions
    setTimeout(() => {
      const chars = titleElement.querySelectorAll('span');
      chars.forEach((char) => {
        char.addEventListener('mouseenter', () => {
          anime({
            targets: char,
            scale: [1, 1.15, 1.08],
            color: [currentColors.text, currentColors.accent, currentColors.text],
            duration: 600,
            easing: 'easeOutCubic'
          });
        });
      });
    }, 6000);

    // Subtitle animation with slide and fade - smoother
    anime({
      targets: subtitleRef.current,
      opacity: [0, 1],
      translateY: [40, 0],
      translateX: [-20, 0],
      scale: [0.95, 1],
      duration: 1000,
      delay: 5200,
      easing: 'easeOutCubic'
    });

    // Tagline animation - smoother
    const taglineText = subtitleRef.current ? personalInfo.tagline : '';
    setTimeout(() => {
      if (taglineRef.current) {
        anime({
          targets: taglineRef.current,
          opacity: [0, 1],
          translateY: [30, 0],
          scale: [0.97, 1],
          duration: 900,
          delay: 6000,
          easing: 'easeOutCubic'
        });
      }
    }, 100);

    // Accent line animation
    anime({
      targets: accentLineRef.current,
      width: ['0%', '100%'],
      opacity: [0, 1],
      duration: 800,
      delay: 7500,
      easing: 'easeOutQuart'
    });

    // Pulsing effect for accent line
    anime({
      targets: accentLineRef.current,
      opacity: [1, 0.5, 1],
      duration: 2000,
      delay: 8000,
      loop: true,
      easing: 'easeInOutSine'
    });

    // Scroll indicator animation with enhanced effects
    anime({
      targets: scrollIndicatorRef.current,
      opacity: [0, 1],
      translateY: [30, 0],
      scale: [0.8, 1],
      duration: 800,
      delay: 8000,
      easing: 'easeOutElastic(1, .6)'
    });

    // Enhanced bounce animation for scroll indicator
    anime({
      targets: scrollIndicatorRef.current,
      translateY: [0, 15, 0],
      scale: [1, 1.1, 1],
      duration: 2500,
      delay: 8500,
      loop: true,
      easing: 'easeInOutSine'
    });

    // Floating animation for the entire hero section
    anime({
      targets: heroRef.current,
      translateY: [0, -5, 0],
      duration: 6000,
      delay: 9000,
      loop: true,
      easing: 'easeInOutSine'
    });
  }, [currentColors.accent]);

  const scrollToAbout = () => {
    // Animate scroll indicator click
    anime({
      targets: scrollIndicatorRef.current,
      scale: [1, 1.2, 1],
      duration: 300,
      easing: 'easeOutQuad'
    });
    
    document.getElementById('about').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" ref={heroRef} className="min-h-screen flex items-center justify-center relative pt-20 overflow-hidden">
      {/* Floating Sparkles */}
      <div ref={sparklesRef} className="absolute inset-0 pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute opacity-0"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              color: currentColors.accent
            }}
          >
            <Sparkles size={16} />
          </div>
        ))}
      </div>

      <div className="container mx-auto px-6 text-center relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Main Title */}
          <h1 
            ref={titleRef}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tight mb-6 select-none font-display px-4 whitespace-nowrap overflow-x-auto"
            style={{ 
              color: currentColors.text
            }}
          >
            {personalInfo.name}
          </h1>

          {/* Subtitle */}
          <h2 
            ref={subtitleRef}
            className="hero-subtitle text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal mb-8 opacity-0 font-sans px-4 cursor-pointer"
            style={{ 
              color: currentColors.text
            }}
            onMouseEnter={(e) => {
              anime({
                targets: e.target,
                scale: [1, 1.05],
                letterSpacing: ['0px', '2px'],
                duration: 400,
                easing: 'easeOutQuart'
              });
            }}
            onMouseLeave={(e) => {
              anime({
                targets: e.target,
                scale: [1.05, 1],
                letterSpacing: ['2px', '0px'],
                duration: 400,
                easing: 'easeOutQuart'
              });
            }}
          >
            {personalInfo.title}
          </h2>

          {/* Tagline */}
          <p 
            ref={taglineRef}
            className="text-sm sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed opacity-0 font-sans px-6"
            style={{ 
              color: currentColors.textSecondary
            }}
          >
            {personalInfo.tagline}
          </p>

          {/* Accent Line */}
          <div className="mt-12 mb-16 flex justify-center">
            <div 
              ref={accentLineRef}
              className="h-0.5 opacity-0"
              style={{ 
                backgroundColor: currentColors.accent,
                width: '96px'
              }}
            />
          </div>
        </div>

        {/* Scroll Indicator - Centered for mobile and desktop */}
        <div className="flex justify-center mt-8">
          <div 
            ref={scrollIndicatorRef}
            className="opacity-0 cursor-pointer group"
            onClick={scrollToAbout}
          >
            <div className="flex flex-col items-center">
              <span 
                className="font-mono text-xs uppercase tracking-wide mb-2 group-hover:scale-110 transition-transform duration-300"
                style={{ color: currentColors.textSecondary }}
              >
                Explore
              </span>
              <div 
                className="p-2 rounded-full border-2 group-hover:scale-110 transition-all duration-300"
                style={{ 
                  borderColor: currentColors.accent,
                  color: currentColors.accent
                }}
              >
                <ChevronDown size={20} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;